import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';

/**
 * GuestActionToast — tiny, in-flow toast that fires when a guest tries to
 * perform a save/track action (heart college, add to compare, etc.).
 *
 * Designed to be informational, not blocking:
 *   • One-line value-anchored message
 *   • Single CTA (sign in)
 *   • Dismiss button always present
 *   • Auto-dismisses after `autoCloseMs` (default 6000ms) so it doesn't linger
 *
 * Render at the global app level via portal or inline at any container.
 */
export default function GuestActionToast({
    show,
    message,
    onClose,
    autoCloseMs = 6000,
    returnTo,
}) {
    useEffect(() => {
        if (!show) return;
        const t = setTimeout(onClose, autoCloseMs);
        return () => clearTimeout(t);
    }, [show, onClose, autoCloseMs]);

    if (!show) return null;

    return (
        <div
            data-testid="guest-action-toast"
            className="fixed z-[60] bottom-4 left-1/2 -translate-x-1/2 w-[min(92vw,26rem)] animate-fade-slide"
        >
            <div className="relative bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3">
                <div className="flex-1 text-sm text-slate-100 leading-snug pr-2">
                    {message}
                </div>
                <Link
                    to={returnTo ? `/register?next=${encodeURIComponent(returnTo)}` : '/register'}
                    data-testid="guest-action-toast-cta"
                    onClick={onClose}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg whitespace-nowrap transition-colors"
                >
                    Sign in <ArrowRight className="w-3 h-3" />
                </Link>
                <button
                    onClick={onClose}
                    data-testid="guest-action-toast-close"
                    aria-label="Dismiss"
                    className="w-7 h-7 inline-flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-800 hover:text-white transition-colors flex-shrink-0"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}
